import { useUserInfo } from "@/api/user";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ROUTES } from "@/constants/routes";
import { useIsCanvasCentricApp } from "@/context/CanvasCentricContext";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import {
  CANVAS_GRID_MODES,
  useCanvasGridModePreference,
  type CanvasGridMode,
} from "@/pages/canvas/canvas-controls.types";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { clearSessionQueryCaches } from "@/queryPersistence";
import { cn } from "@/utils";
import type { OpenSettingsInput } from "@/utils/settingsNavigation";
import { useAuth0 } from "@auth0/auth0-react";
import { useThemeContext } from "@cohesive/ui-components";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Archive,
  ArrowLeftRight,
  LayoutGrid,
  LogOut,
  Settings,
  SunMoon,
  UserRound,
  UserRoundPlus,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
// Comment
function OrganizationIcon({
  org,
  onClick,
}: {
  org: { name: string; logo_url?: string };
  onClick?: () => void;
}) {
  const body = org.logo_url ? (
    <img
      src={org.logo_url}
      alt={`${org.name} logo`}
      className="h-8 w-8 rounded-lg object-cover"
    />
  ) : (
    <div
      className="flex h-8 w-8 items-center justify-center rounded-lg
                 bg-foreground/[0.06] dark:bg-n-95/[0.06]"
    >
      <span className="text-sm font-medium text-n-90">
        {org.name.charAt(0)}
      </span>
    </div>
  );

  if (!onClick) return <div className="shrink-0">{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Organization settings"
      className="group/orgicon relative shrink-0 rounded-lg cursor-pointer
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {body}
      <span
        className="absolute inset-0 flex items-center justify-center rounded-lg
                   bg-n-95/55 text-n-00 opacity-0
                   group-hover/orgicon:opacity-100 focus-visible:opacity-100
                   transition-opacity duration-150"
      >
        <Settings className="h-4 w-4" />
      </span>
    </button>
  );
}

export interface AccountPopoverProps {
  children: React.ReactNode;
  /** When provided, opens settings via this callback instead of the context hook. */
  onOpenSettings?: (input?: OpenSettingsInput) => void;
  /** Popover placement. If undefined, uses responsive: right on md+, bottom on smaller. */
  popoverSide?: "right" | "bottom" | "top";
  /** Content alignment relative to the trigger. Default `"start"`. Use `"end"`
   *  for top-right triggers — and to bottom-align a side="right" quick-nav to
   *  the rail's footer corner (DS §12). */
  popoverAlign?: "start" | "center" | "end";
  /**
   * When false, omit app-only actions (settings, billing management, canvas-centric
   * toggle) and do not require `CanvasCentricProvider`. Default true.
   */
  showAppOnlyActions?: boolean;
}

/** Isolated so AccountPopover can skip CanvasCentricProvider on onboarding routes. */
function CanvasCentricToggleRow({
  separatorClassName,
}: {
  separatorClassName: string;
}) {
  const navigate = useNavigate();
  const { isCanvasCentricApp, setCanvasCentricApp } = useIsCanvasCentricApp();

  return (
    <>
      <DropdownMenuSeparator className={separatorClassName} />
      <div className="flex min-h-8 items-center justify-between gap-2 rounded-xs px-2">
        <Label
          htmlFor="canvas-centric-toggle"
          className="flex flex-1 cursor-pointer items-center gap-2 text-base font-normal text-n-80"
        >
          <LayoutGrid className="size-3.5 shrink-0 text-n-50" />
          <span>Canvas app</span>
        </Label>
        <Switch
          id="canvas-centric-toggle"
          checked={isCanvasCentricApp}
          onCheckedChange={(checked) => {
            setCanvasCentricApp(!!checked);
            navigate(ROUTES.HOME);
          }}
        />
      </div>
    </>
  );
}

export function AccountPopover({
  children,
  onOpenSettings,
  popoverSide,
  popoverAlign = "start",
  showAppOnlyActions = true,
}: AccountPopoverProps) {
  const md = useBreakpoint("md");
  const { data: userInfo } = useUserInfo();
  const isOrgAdmin = useIsOrgAdmin();
  const { logout, loginWithRedirect } = useAuth0();
  const flags = useFlags<Flags>();
  const contextOpenSettings = useOpenSettings();
  const { preference, setPreference } = useThemeContext();
  const [gridMode, setGridMode] = useCanvasGridModePreference();

  const openSettings = onOpenSettings ?? contextOpenSettings;
  // Nothing to switch to on a single-org account, so the row stays hidden
  // there rather than opening a picker with one entry.
  const isMultiOrg = (userInfo?.organizations?.length ?? 0) > 1;

  const handleLogout = () => {
    clearSessionQueryCaches();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const side = popoverSide ?? (md ? "right" : "bottom");

  /**
   * The design system's menu-item register. The shared primitive is tuned for
   * the app's older menus (12px text, accent-tinted highlight, 16px icons);
   * these classes bring an item back to the DS row: 32px, 14px sans, the n-05
   * highlight wash, 14px icons.
   */
  const dsItem =
    // Row register borrowed from the left rail so the highlight reads the same
    // on both surfaces — same n-05 fill, same rounded-md pill.
    "min-h-8 cursor-pointer select-none rounded-md py-0 text-base text-n-80 hover:bg-n-05 hover:text-n-80 focus:bg-n-05 focus:text-n-80 data-[highlighted]:bg-n-05 [&_svg]:size-3.5";
  const dsSeparator = "mx-0 bg-n-10";
  // The quick-nav flies out right of the rail's edge, its bottom floating a
  // breath above the identity row. Collision avoidance is off deliberately:
  // Radix's limitShift would clamp that lift back down to the trigger.
  const placement =
    side === "right"
      ? ({ sideOffset: 8, alignOffset: 64, avoidCollisions: false } as const)
      : ({ sideOffset: 8, collisionPadding: 20 } as const);

  return (
    /**
     * Non-modal deliberately. Half these rows open the Manage modal, and a
     * modal menu locks `pointer-events` on the body while it closes — so the
     * dialog mounted into a locked document, and when it later closed it
     * released a lock the menu still owned. The app came back dead to every
     * click. A menu has no reason to trap the page anyway; dismissal and
     * Escape are the dismissable layer's job, not the lock's.
     */
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid="user-avatar-popover"
        side={side}
        align={popoverAlign}
        {...placement}
        className="w-56 border-n-10 bg-popover shadow-[var(--elev-3)]"
      >
        <div className="px-2 py-1.5">
          <div className="truncate text-sm font-medium text-n-90">
            {userInfo?.user.name}
          </div>
          <div className="truncate text-xs text-n-50">
            {userInfo?.user.email}
          </div>
        </div>

        {/* The rail's identity line names the organization in the app; on
            onboarding there is no rail, so the menu names it. */}
        {!showAppOnlyActions && userInfo?.organization && (
          <>
            <DropdownMenuSeparator className={dsSeparator} />
            <div className="flex items-center gap-2 px-2 py-1.5">
              <OrganizationIcon org={userInfo.organization} />
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-n-90">
                {userInfo.organization.name}
              </p>
            </div>
          </>
        )}

        {/* The personal store — who you are, and what you put away. */}
        {showAppOnlyActions && (
          <>
            <DropdownMenuSeparator className={dsSeparator} />
            <DropdownMenuItem
              className={dsItem}
              onSelect={() => openSettings(ROUTES.SETTINGS.PROFILE)}
            >
              <UserRound className="size-3.5 text-n-50" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              className={dsItem}
              onSelect={() => openSettings(ROUTES.SETTINGS.ARCHIVE)}
            >
              <Archive className="size-3.5 text-n-50" />
              Archive
            </DropdownMenuItem>
          </>
        )}

        {/* Live shell state: a pick here re-themes the surface and re-grids
            every canvas, so the menu stays open while you choose. */}
        <DropdownMenuSeparator className={dsSeparator} />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            className={cn(dsItem, "data-[state=open]:bg-n-05")}
          >
            <SunMoon className="size-3.5 text-n-50" />
            Appearance
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-32 border-n-10 bg-popover shadow-[var(--elev-3)]">
            <DropdownMenuRadioGroup
              value={preference}
              onValueChange={(value) =>
                setPreference(value as "light" | "dark" | "system")
              }
            >
              {(["light", "dark", "system"] as const).map((value) => (
                <DropdownMenuRadioItem
                  key={value}
                  value={value}
                  className={cn(
                    dsItem,
                    "pl-7 [&>span:first-child]:size-3.5 [&>span:first-child_svg]:size-2",
                  )}
                  onSelect={(event) => event.preventDefault()}
                >
                  {value === "system"
                    ? "System"
                    : value.charAt(0).toUpperCase() + value.slice(1)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {showAppOnlyActions && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className={cn(dsItem, "data-[state=open]:bg-n-05")}
            >
              <LayoutGrid className="size-3.5 text-n-50" />
              Canvas grid
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-32 border-n-10 bg-popover shadow-[var(--elev-3)]">
              <DropdownMenuRadioGroup
                value={gridMode}
                onValueChange={(value) => setGridMode(value as CanvasGridMode)}
              >
                {CANVAS_GRID_MODES.map((option) => (
                  <DropdownMenuRadioItem
                    key={option.key}
                    value={option.key}
                    className={cn(
                      dsItem,
                      "pl-7 [&>span:first-child]:size-3.5 [&>span:first-child_svg]:size-2",
                    )}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {option.key === "lines"
                      ? "Lines"
                      : option.key === "dots"
                        ? "Dots"
                        : "Off"}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        <DropdownMenuSeparator className={dsSeparator} />
        {showAppOnlyActions && isOrgAdmin && (
          <DropdownMenuItem
            className={dsItem}
            onSelect={() => openSettings(ROUTES.SETTINGS.MEMBERS)}
          >
            <UserRoundPlus className="size-3.5 text-n-50" />
            Invite members
          </DropdownMenuItem>
        )}
        {isMultiOrg && (
          <DropdownMenuItem
            className={dsItem}
            onSelect={() =>
              loginWithRedirect({
                authorizationParams: {
                  prompt: "select_account",
                  ignoreCache: true,
                },
              })
            }
          >
            <ArrowLeftRight className="size-3.5 text-n-50" />
            Switch organization
          </DropdownMenuItem>
        )}
        {showAppOnlyActions && (
          <DropdownMenuItem
            className={dsItem}
            onSelect={() =>
              // Land where the org lives, but only for someone who can see
              // it — Organization is admin-gated, and sending a member to a
              // scope missing from their rail lands them nowhere.
              openSettings(
                isOrgAdmin
                  ? ROUTES.SETTINGS.ORGANIZATION
                  : ROUTES.SETTINGS.PROFILE,
              )
            }
          >
            <Settings className="size-3.5 text-n-50" />
            Settings
          </DropdownMenuItem>
        )}

        {showAppOnlyActions && flags.canvasCentricAppAdminToggle && (
          <CanvasCentricToggleRow separatorClassName={dsSeparator} />
        )}

        <DropdownMenuSeparator className={dsSeparator} />
        <DropdownMenuItem
          className={cn(
            dsItem,
            "text-spectrum-red hover:text-spectrum-red focus:text-spectrum-red data-[highlighted]:text-spectrum-red",
          )}
          onSelect={handleLogout}
        >
          <LogOut className="size-3.5" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
